/**
 * The account menu, and the only sign-out in the app.
 *
 * It was missing entirely, which matters more here than in most products: this platform's
 * own argument for forcing a password change is that a shared credential makes the audit
 * trail meaningless. A console that shows no signed-in identity and offers no way to leave
 * invites exactly the shared-session habit that argument exists to prevent.
 *
 * So the menu states WHO and AS WHAT on the way to the sign-out, not just the button.
 * Role is included because it is why an action is missing — an auditor who cannot see the
 * Integrate button has no other way to learn that is deliberate.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useAuth } from '@/lib/auth-context';
import { roleLabel, roleScope, useMe } from '@/lib/api/me';
import { Icon } from './Icon';

export function AccountMenu(): ReactNode {
  const { user, signOut } = useAuth();
  const me = useMe();
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const root = useRef<HTMLDivElement | null>(null);

  /* Dismiss on outside click and on Escape — a menu that only closes by re-clicking its
     own trigger is the classic way to trap someone behind an overlay. */
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const email = me.data?.user.email ?? user?.email ?? 'unknown';
  const name = me.data?.user.fullName;
  const role = me.data?.user.role;
  const org = me.data?.organisation.name;
  const initial = (name ?? email).trim().charAt(0).toUpperCase() || '?';

  return (
    <div ref={root} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="lm-hov"
        title={`Signed in as ${email}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          height: 'var(--header-ctl-h)',
          paddingInline: 4,
          paddingRight: 8,
          borderRadius: 'var(--radius-sm)',
          border: `1px solid ${open ? 'var(--ring)' : 'var(--border)'}`,
          background: open ? 'var(--muted)' : 'var(--card)',
        }}
      >
        <span
          className="lm-num"
          aria-hidden="true"
          style={{
            width: 22,
            height: 22,
            borderRadius: 6,
            display: 'grid',
            placeItems: 'center',
            background: 'var(--brand)',
            color: 'var(--brand-fg)',
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          {initial}
        </span>
        <Icon name={open ? 'ic-chevup' : 'ic-chevdown'} size={13} />
      </button>

      {open && (
        <div
          role="menu"
          className="lm-card"
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 6px)',
            minWidth: 268,
            zIndex: 50,
            padding: 0,
            overflow: 'hidden',
          }}
        >
          {/* -------------------------------------------------------- identity */}
          <div className="border-b p-[12px_14px]" style={{ borderColor: 'var(--border)' }}>
            <div className="text-[13px] font-bold" style={{ wordBreak: 'break-all' }}>
              {name ?? email}
            </div>
            {name && (
              <div className="text-[11.5px]" style={{ color: 'var(--muted-fg)', wordBreak: 'break-all' }}>
                {email}
              </div>
            )}
            {org && (
              <div className="mt-[6px] flex items-center gap-[6px] text-[11.5px]" style={{ color: 'var(--muted-fg)' }}>
                <Icon name="ic-building" size={12} />
                {org}
              </div>
            )}
          </div>

          {/* ------------------------------------------------------------ role */}
          {role && (
            <div className="border-b p-[10px_14px]" style={{ borderColor: 'var(--border)' }}>
              <div className="flex items-center justify-between gap-[10px]">
                <span
                  className="text-[12px] font-semibold"
                  style={{ color: 'var(--muted-fg)' }}
                >
                  Role
                </span>
                <span
                  className="rounded-[var(--radius-pill)] px-[7px] py-[1px] text-[11px] font-bold"
                  style={{ background: 'var(--secondary)', color: 'var(--fg)' }}
                >
                  {roleLabel(role)}
                </span>
              </div>
              {/* Why a button is missing. Without this the UI just looks broken to a
                  read-only user. */}
              <p className="mt-[5px] text-[11.5px] leading-[1.45]" style={{ color: 'var(--muted-fg)' }}>
                {roleScope(role)}
              </p>
            </div>
          )}

          {/* -------------------------------------------------------- sign out */}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setSigningOut(true);
              void signOut();
            }}
            disabled={signingOut}
            className="lm-hov flex w-full items-center gap-[9px] p-[11px_14px] text-left text-[13px] font-semibold"
            style={{ color: 'var(--danger)' }}
          >
            <Icon name="ic-logout" size={15} />
            {signingOut ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      )}
    </div>
  );
}
